import { useState, useEffect } from 'react';
import { useTheme } from '../ThemeContext';

const PASSKEY = 'pcodes_great';
const STORAGE_KEY = 'fiveg_access_granted';
const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes

export default function ProtectedRoute({ children, pageName = 'This Page' }) {
  const { dark } = useTheme();
  const [unlocked, setUnlocked] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);
  const [lockTimer, setLockTimer] = useState(0);
  const [show, setShow] = useState(false);
  const [dots, setDots] = useState([]);

  // Check if already unlocked this session
  useEffect(() => {
    try {
      const d = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (d && d.granted && Date.now() - d.time < SESSION_DURATION) {
        setUnlocked(true);
      }
    } catch (e) {}

    // Animate background dots
    const arr = Array.from({ length: 18 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 2 + Math.random() * 4,
      delay: Math.random() * 3,
      dur: 3 + Math.random() * 4,
    }));
    setDots(arr);
  }, []);

  // Lockout countdown
  useEffect(() => {
    if (!locked) return;
    const interval = setInterval(() => {
      setLockTimer(t => {
        if (t <= 1) { setLocked(false); setAttempts(0); clearInterval(interval); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [locked]);

  const handleSubmit = e => {
    e.preventDefault();
    if (locked) return;
    if (input === PASSKEY) {
      setError('');
      setUnlocked(true);
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ granted: true, time: Date.now() }));
      } catch (e) {}
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setError(newAttempts >= 3 ? 'Too many attempts.' : 'Incorrect passkey. Please try again.');
      setShake(true);
      setTimeout(() => setShake(false), 600);
      setInput('');
      if (newAttempts >= 3) {
        setLocked(true);
        setLockTimer(30);
      }
    }
  };

  if (unlocked) return children;

  const bg      = dark ? '#060c18' : '#f4f8ff';
  const border  = dark ? 'rgba(0,212,255,0.15)' : 'rgba(0,120,200,0.18)';
  const accent  = dark ? '#00d4ff' : '#0078c8';
  const text1   = dark ? '#e8f4ff' : '#0a1e3d';
  const text2   = dark ? '#8ab4d4' : '#2a4a6a';
  const text3   = dark ? '#4a7090' : '#6080a0';
  const cardBg  = dark ? '#0d1929' : '#ffffff';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&family=Inter:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box;}
        .pg-root{
          min-height:100vh;background:${bg};display:flex;align-items:center;
          justify-content:center;padding:80px 20px 40px;
          font-family:'Inter',sans-serif;position:relative;overflow:hidden;
        }
        .pg-root::before{
          content:'';position:fixed;inset:0;
          background-image:linear-gradient(${dark?'rgba(0,212,255,0.04)':'rgba(0,120,200,0.05)'} 1px,transparent 1px),
            linear-gradient(90deg,${dark?'rgba(0,212,255,0.04)':'rgba(0,120,200,0.05)'} 1px,transparent 1px);
          background-size:52px 52px;pointer-events:none;
        }
        .pg-dot{
          position:absolute;border-radius:50%;
          background:${accent};pointer-events:none;
          animation:pgdrift var(--dur,4s) ease-in-out var(--delay,0s) infinite alternate;
        }
        @keyframes pgdrift{
          0%{transform:translate(0,0);opacity:0.15;}
          100%{transform:translate(20px,-30px);opacity:0.55;}
        }
        .pg-card{
          position:relative;z-index:1;
          width:100%;max-width:420px;
          background:${cardBg};
          border:1px solid ${border};
          border-radius:12px;padding:40px 36px;
          text-align:center;
        }
        .pg-card::before{
          content:'';position:absolute;top:0;left:0;right:0;height:2px;
          background:linear-gradient(90deg,transparent,${accent},transparent);
          border-radius:12px 12px 0 0;
        }
        @keyframes pgshake{
          0%,100%{transform:translateX(0);}
          20%{transform:translateX(-8px);}
          40%{transform:translateX(8px);}
          60%{transform:translateX(-6px);}
          80%{transform:translateX(6px);}
        }
        .pg-card.shake{animation:pgshake 0.5s ease;}
        .lock-icon{
          width:64px;height:64px;border-radius:14px;
          background:${dark?'rgba(0,212,255,0.08)':'rgba(0,120,200,0.07)'};
          border:1px solid ${border};
          display:flex;align-items:center;justify-content:center;
          margin:0 auto 20px;font-size:28px;
          animation:lockhover 3s ease-in-out infinite;
        }
        @keyframes lockhover{
          0%,100%{transform:translateY(0);}
          50%{transform:translateY(-5px);}
        }
        .pg-input{
          width:100%;background:${dark?'rgba(0,212,255,0.04)':'rgba(0,0,0,0.03)'};
          border:1px solid ${border};border-radius:6px;
          padding:12px 14px;font-size:15px;color:${text1};
          font-family:'Inter',sans-serif;outline:none;
          transition:border-color 0.2s,box-shadow 0.2s;
          letter-spacing:2px;text-align:center;margin-bottom:14px;
        }
        .pg-input::placeholder{letter-spacing:1px;color:${text3};font-size:13px;}
        .pg-input:focus{border-color:${accent};box-shadow:0 0 0 3px ${dark?'rgba(0,212,255,0.1)':'rgba(0,120,200,0.1)'};}
        .pg-input:disabled{opacity:0.5;}
        .pg-btn{
          width:100%;background:${accent};color:${dark?'#020810':'#fff'};
          font-weight:700;font-size:13px;letter-spacing:1.5px;
          text-transform:uppercase;border:none;border-radius:6px;
          padding:13px;cursor:none;transition:all 0.2s;
          font-family:'Inter',sans-serif;
        }
        .pg-btn:hover:not(:disabled){filter:brightness(1.1);transform:translateY(-1px);}
        .pg-btn:disabled{opacity:0.5;cursor:not-allowed;}
        .pg-error{
          background:${dark?'rgba(255,68,85,0.1)':'rgba(255,68,85,0.07)'};
          border:1px solid rgba(255,68,85,0.3);
          color:#ff4455;border-radius:6px;
          padding:10px 14px;font-size:12px;
          margin-top:12px;
        }
        .pg-hint{
          font-size:11px;color:${text3};margin-top:14px;
          line-height:1.6;letter-spacing:0.3px;
        }
        .pg-page-name{
          font-family:'Orbitron',monospace;font-size:11px;
          letter-spacing:3px;color:${accent};text-transform:uppercase;
          margin-bottom:16px;
        }
        .pg-title{
          font-family:'Orbitron',monospace;
          font-size:clamp(18px,4vw,24px);font-weight:900;
          color:${text1};margin-bottom:8px;line-height:1.2;
        }
        .pg-sub{font-size:13px;color:${text2};line-height:1.65;margin-bottom:28px;}
        .lock-timer{
          font-family:'Orbitron',monospace;font-size:24px;
          font-weight:700;color:#ff4455;margin-bottom:8px;
        }
        .attempts-row{
          display:flex;justify-content:center;gap:6px;margin-bottom:20px;
        }
        .att-dot{
          width:8px;height:8px;border-radius:50%;
          background:${border};transition:background 0.2s;
        }
        .att-dot.used{background:#ff4455;}
      `}</style>

      <div className="pg-root">
        {dots.map(d => (
          <div
            key={d.id}
            className="pg-dot"
            style={{
              left: `${d.x}%`, top: `${d.y}%`,
              width: d.size, height: d.size,
              '--dur': `${d.dur}s`,
              '--delay': `${d.delay}s`,
            }}
          />
        ))}

        <div className={`pg-card${shake ? ' shake' : ''}`}>
          <div className="lock-icon">🔐</div>
          <div className="pg-page-name">{pageName}</div>
          <div className="pg-title">Restricted Access</div>
          <p className="pg-sub">
            This section contains confidential research data and system architecture details.
            Enter your passkey to continue.
          </p>

          {/* Attempt dots */}
          <div className="attempts-row">
            {[0, 1, 2].map(i => (
              <div key={i} className={`att-dot${i < attempts ? ' used' : ''}`} />
            ))}
          </div>

          {locked ? (
            <div style={{ marginBottom: 8 }}>
              <div className="lock-timer">{lockTimer}s</div>
              <div style={{ fontSize: 12, color: '#ff4455', marginBottom: 16 }}>
                Too many failed attempts. Please wait.
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <input
                type={show ? 'text' : 'password'}
                value={input}
                onChange={e => { setInput(e.target.value); setError(''); }}
                placeholder="Enter passkey"
                className="pg-input"
                autoComplete="off"
                autoFocus
                disabled={locked}
              />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 14, marginTop: -8 }}>
                <button
                  type="button"
                  onClick={() => setShow(s => !s)}
                  style={{ background: 'none', border: 'none', color: text3, fontSize: 11, cursor: 'none', letterSpacing: 0.5, padding: '2px 0' }}
                >
                  {show ? 'Hide' : 'Show'} passkey
                </button>
              </div>
              <button type="submit" className="pg-btn" disabled={!input || locked}>
                Unlock Access
              </button>
            </form>
          )}

          {error && <div className="pg-error">{error}{locked && ` Try again in ${lockTimer}s.`}</div>}

          <p className="pg-hint">
            Access is granted for the current browser session only.<br />
            Contact the developer if you need permanent access.
          </p>
        </div>
      </div>
    </>
  );
}
